import React from 'react'
import { List, useTable, ShowButton, DeleteButton, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderAvatarName } from '../../theme/columns'
import { useRowShow, ListPage, Pill, TableSurface, type Stat, type Tone } from '../../theme/listkit'
import { Bell, Activity, UsersRound } from '../../components/icons'

type Notification = Tables<'notifications'>
type Profile = Tables<'profiles'>

interface NotificationsSummary {
  total_notifications: number | null
  unseen_count: number | null
  distinct_recipients: number | null
}

/** Group chips lean into the recency story — fresh today, warmer this week. */
const grpTone = (grp: string): Tone => {
  const g = grp.toLowerCase()
  if (g.includes('today')) return 'green'
  if (g.includes('week')) return 'jaguar'
  return 'neutral'
}

export function NotificationList() {
  const { tableProps } = useTable<Notification>({
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })
  const onRow = useRowShow('notifications')

  // Stat deck: a single pre-aggregated row from the admin summary VIEW instead of
  // fetching every notification (mode:'off') and counting/Set-ing on the main thread.
  const { result: summaryRes } = useList<NotificationsSummary>({
    resource: 'admin_notifications_summary',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const summary = summaryRes?.data?.[0]

  // Resolve user_id -> name, scoped to the CURRENT page's recipients only (was a
  // whole-table useSelect over profiles). Disabled until there are ids to resolve.
  const pageUserIds = React.useMemo(
    () =>
      Array.from(
        new Set(((tableProps.dataSource ?? []) as Notification[]).map((n) => n.user_id).filter(Boolean)),
      ) as string[],
    [tableProps.dataSource],
  )
  const { result: profileRes } = useList<Profile>({
    resource: 'profiles',
    filters: pageUserIds.length ? [{ field: 'user_id', operator: 'in', value: pageUserIds }] : [],
    pagination: { mode: 'off' },
    liveMode: 'off',
    queryOptions: { enabled: pageUserIds.length > 0 },
  })
  const profileMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileRes?.data ?? []) as Profile[]
    return Object.fromEntries(items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]))
  }, [profileRes])

  const total = summary?.total_notifications ?? 0
  const unseen = summary?.unseen_count ?? 0
  const recipients = summary?.distinct_recipients ?? 0

  // Stat label is "Unseen" (NOT "Unread") so it never collides with the row pill text.
  const stats: Stat[] = [
    { label: 'Notifications', value: total.toLocaleString(), icon: Bell, tone: 'gold' },
    { label: 'Unseen', value: unseen.toLocaleString(), hint: 'awaiting open', icon: Activity, tone: 'red' },
    { label: 'Recipients', value: recipients.toLocaleString(), hint: 'distinct members', icon: UsersRound, tone: 'jaguar' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Messaging · In-app Notifications',
        description:
          'Every push and in-app alert sent to members — homecoming reminders, gift receipts, and class news. Track reach, see what is still unopened, and resolve each recipient.',
      }}
      stats={stats}
      statColumns={3}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Notification>
              dataIndex="user_id"
              title="Recipient"
              render={(userId: string) => renderAvatarName(profileMap[userId] ?? userId)}
            />
            <Table.Column<Notification>
              dataIndex="grp"
              title="Group"
              render={(grp: string) => (grp ? <Pill tone={grpTone(grp)}>{grp}</Pill> : '—')}
            />
            <Table.Column<Notification>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{title}</span>
              )}
            />
            <Table.Column<Notification>
              dataIndex="unread"
              title="State"
              render={(unread: boolean) =>
                unread ? <Pill tone="red">Unread</Pill> : <Pill tone="neutral">Read</Pill>
              }
            />
            <Table.Column<Notification>
              dataIndex="created_at"
              title="Sent"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Notification>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Notification) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
